import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { paymentsApi } from "../services/payments";

/**
 * SubscriptionContext — single source of truth for "is this user paid?"
 * Fetches /api/me/subscription on auth changes; exposes:
 *   - active (bool)
 *   - tier   (STANDARD | PREMIUM | ENTERPRISE | null)
 *   - subscription (full object, incl. features map)
 *   - wallet ({ monthly_balance, topup_balance })   - legacy, kept for old data
 *   - refresh()
 *
 * Token wallets were retired Feb 2026 — all paid plans now ship unlimited
 * AI queries. The wallet field is kept on the context for legacy
 * subscriptions; new subs persist `features.ai_queries === "unlimited"`.
 */
const SubscriptionContext = createContext({
  active: false,
  tier: null,
  subscription: null,
  wallet: { monthly_balance: 0, topup_balance: 0 },
  loading: true,
  refresh: () => {},
});

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    active: false, tier: null, subscription: null,
    wallet: { monthly_balance: 0, topup_balance: 0 },
    loading: true,
  });

  const refresh = useCallback(async () => {
    if (!user) {
      setState({
        active: false, tier: null, subscription: null,
        wallet: { monthly_balance: 0, topup_balance: 0 },
        loading: false,
      });
      return;
    }
    // Admin (staff) gets unrestricted, free access to all paid features.
    // This bypasses every `paidOnly()` gate, the Analyst-Mode upsell modal,
    // the AI insight subscription requirement, and the PDF export locks.
    // The backend independently grants the same bypass via role checks.
    if ((user.role || "").toLowerCase() === "admin") {
      setState({
        active: true,
        tier: "ADMIN",
        subscription: {
          plan_id: "ADMIN_OVERRIDE",
          tier: "ADMIN",
          billing_cycle: "NONE",
          geography_ids: [],
          geography_count: 12,
          combined_authority: null,
          features: {
            mode_access: ["STANDARD", "ANALYST"],
            ai_queries: "unlimited",
            reports: ["single_la", "multi_la", "ca_aggregate", "multi_ca"],
            scenario_modelling: true,
            ca_intelligence: true,
            compare_authorities_limit: null,
            admin_override: true,
          },
          current_period_end: null,
        },
        wallet: { monthly_balance: 0, topup_balance: 0 },
        loading: false,
      });
      return;
    }
    try {
      const data = await paymentsApi.mySubscription();
      setState({
        active: !!data.active,
        tier: data.subscription?.tier || null,
        subscription: data.subscription || null,
        wallet: data.wallet || { monthly_balance: 0, topup_balance: 0 },
        loading: false,
      });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <SubscriptionContext.Provider value={{ ...state, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  return useContext(SubscriptionContext);
}
